import {TmOwnershipTransferTrademarkDetails} from '@/components/trademark-ownership-transfer/form/types'
import {Jurisdiction} from '@/utils/countries/constants'
import {Currency} from '@/utils/currency'
import {
  billingEntities,
  BillingEntityId,
  calculateVatStatus,
  createFeeItem,
  Fee,
  sumFees,
  sumPrices,
} from '@/utils/pricing'
import {findCatalogProduct, getUnitPrice, hasTieredPricing} from '@/utils/pricing/catalog'
import {buildProductFee} from '@/utils/pricing/product-fee'
import {TrademarkOwnershipTransferJurisdiction} from '@/utils/trademark-ownership-transfer/constants'
import {BillingEntityType} from '@/utils/validations/common/billingEntity'

import {add, sub} from '../decimals'
import {
  AssignmentAgreementServiceType,
  TmOwnershipTransferCountryPrice,
  TmOwnershipTransferJurisdictionsPrice,
  TmOwnershipTransferOrderPrice,
} from './types'

// Catalog categories backing an assignment: our service fee and the paired
// government fee, both keyed by jurisdiction. The per-trademark tiers from the
// assignment category sheets are folded into each product's price rules, so
// getUnitPrice(product, n) is the full price for n trademarks — the same
// "base + cumulative tiers" arithmetic the sheets encoded.
const ASSIGNMENT_SERVICE_CATEGORY = 'assignmentRegistrationMargins'
const ASSIGNMENT_GOV_CATEGORY = 'assignmentRegistrationFees'

// Tolerant catalog price for `numOfTrademarks`; 0 when the jurisdiction has no
// product on this side (an all-zero sheet row is not synced as a product).
const assignmentUnitPrice = (
  category: string,
  jurisdiction: TrademarkOwnershipTransferJurisdiction,
  currency: Currency,
  numOfTrademarks: number,
): number => {
  const product = findCatalogProduct(category, jurisdiction)

  return product ? getUnitPrice(product, numOfTrademarks, currency) : 0
}

// One trademark's fee at its 1-indexed position, bound to its catalog product:
// label, gov flag and product id all come from the product; the amount is the
// marginal step from (position - 1) to position, so the 1st trademark carries the
// jurisdiction's base price and each later one only its tier. Null when the
// jurisdiction has no product on this side.
const assignmentTrademarkFee = (
  category: string,
  jurisdiction: TrademarkOwnershipTransferJurisdiction,
  currency: Currency,
  position: number,
  vatRate: number,
): Fee | null => {
  const product = findCatalogProduct(category, jurisdiction)

  // No product means this jurisdiction has no fee on this side — expected.
  if (!product) {
    return null
  }
  // A product that exists but has no invoice line text is non-invoiceable and
  // unorderable, so reaching it here is a bug. Fail rather than silently drop
  // the line from a priced order.
  if (!product.invoice_translation_key) {
    throw new Error(`Catalog product "${category}.${jurisdiction}" has no invoice_translation_key`)
  }

  const priceAt = (count: number) => (count > 0 ? getUnitPrice(product, count, currency) : 0)

  return createFeeItem(
    product.invoice_translation_key,
    sub(priceAt(position), priceAt(position - 1)),
    1,
    product.is_gov_fee ? 0 : vatRate,
    product.is_gov_fee,
    product.id,
  )
}

// Pricing-page display fees keep their table-header labels (they are never quote
// lines), but read their amounts from the catalog so the displayed price can't
// drift from what the order path charges.
const calculateTmOwnershipTransferServiceFee = (
  jurisdiction: TrademarkOwnershipTransferJurisdiction,
  currency: Currency,
  vatRate: number,
  numOfTrademarks: number,
): Fee =>
  createFeeItem(
    'pricing-reg-table-header-1',
    assignmentUnitPrice(ASSIGNMENT_SERVICE_CATEGORY, jurisdiction, currency, numOfTrademarks),
    1,
    vatRate,
    false,
  )

const calculateTmOwnershipTransferLegalFees = (
  jurisdiction: TrademarkOwnershipTransferJurisdiction,
  currency: Currency,
  numOfTrademarks: number,
): Fee | null => {
  if (!findCatalogProduct(ASSIGNMENT_GOV_CATEGORY, jurisdiction)) {
    return null
  }

  return createFeeItem(
    'pricing-reg-table-header-2',
    assignmentUnitPrice(ASSIGNMENT_GOV_CATEGORY, jurisdiction, currency, numOfTrademarks),
    1,
    0,
    true,
  )
}

export const calculateTmOwnershipTransferCountryPrice = (
  jurisdiction: TrademarkOwnershipTransferJurisdiction,
  currency: Currency,
  numOfTrademarks: number = 1,
): TmOwnershipTransferCountryPrice => {
  const serviceFee = calculateTmOwnershipTransferServiceFee(
    jurisdiction,
    currency,
    0,
    numOfTrademarks,
  )
  const legalFee = calculateTmOwnershipTransferLegalFees(jurisdiction, currency, numOfTrademarks)

  const total = sumFees([serviceFee].concat(legalFee ? [legalFee] : []))

  return {
    serviceFee,
    ...(legalFee ? {legalFee} : {}),
    total,
  }
}

export const calculateTmOwnershipTransferAdditionalTrademarkFee = (
  jurisdiction: TrademarkOwnershipTransferJurisdiction,
  currency: Currency,
): number | null => {
  const hasBreakpoints = [ASSIGNMENT_SERVICE_CATEGORY, ASSIGNMENT_GOV_CATEGORY].some((category) => {
    const product = findCatalogProduct(category, jurisdiction)

    return product ? hasTieredPricing([product]) : false
  })

  if (!hasBreakpoints) {
    return null
  }

  // the 2nd trademark's marginal fee - same delta calculateTmOwnershipTransferTrademarkFees
  // computes internally for any position, so reuse it instead of diffing two full country prices
  const {serviceFee, legalFee} = calculateTmOwnershipTransferTrademarkFees(
    jurisdiction,
    currency,
    2,
    0,
  )

  return add(serviceFee.total.vatBase, legalFee?.total.vatBase ?? 0)
}

// Billing fees for a whole jurisdiction: one service line and (where the
// jurisdiction has a government product) one government line, priced for
// `numberOfTrademarks` at once. Catalog-bound, so the quote lines carry
// product_id and take their label from the product.
const calculateTmOwnershipTransferJurisdictionFees = (
  jurisdiction: TrademarkOwnershipTransferJurisdiction,
  currency: Currency,
  numberOfTrademarks: number,
  vatRate: number,
): {serviceFee: Fee; legalFee?: Fee} => {
  const feeFor = (category: string): Fee | null => {
    const product = findCatalogProduct(category, jurisdiction)

    if (!product) {
      return null
    }
    if (!product.invoice_translation_key) {
      throw new Error(
        `Catalog product "${category}.${jurisdiction}" has no invoice_translation_key`,
      )
    }

    return createFeeItem(
      product.invoice_translation_key,
      getUnitPrice(product, numberOfTrademarks, currency),
      1,
      product.is_gov_fee ? 0 : vatRate,
      product.is_gov_fee,
      product.id,
    )
  }

  const serviceFee = feeFor(ASSIGNMENT_SERVICE_CATEGORY)

  if (!serviceFee) {
    throw new Error(`No catalog product for sku "${ASSIGNMENT_SERVICE_CATEGORY}.${jurisdiction}"`)
  }

  const legalFee = feeFor(ASSIGNMENT_GOV_CATEGORY)

  return {serviceFee, ...(legalFee ? {legalFee} : {})}
}

// Display-only breakdown for the form summary: each trademark's marginal cost at
// its 1-indexed position within its jurisdiction's group, so the customer can see
// that (say) a second UK mark adds nothing. Never persisted — billing uses the
// per-jurisdiction lines above.
const calculateTmOwnershipTransferTrademarkFees = (
  jurisdiction: TrademarkOwnershipTransferJurisdiction,
  currency: Currency,
  position: number,
  vatRate: number,
): {serviceFee: Fee; legalFee?: Fee} => {
  const serviceFee = assignmentTrademarkFee(
    ASSIGNMENT_SERVICE_CATEGORY,
    jurisdiction,
    currency,
    position,
    vatRate,
  )

  if (!serviceFee) {
    throw new Error(`No catalog product for sku "${ASSIGNMENT_SERVICE_CATEGORY}.${jurisdiction}"`)
  }

  const legalFee = assignmentTrademarkFee(
    ASSIGNMENT_GOV_CATEGORY,
    jurisdiction,
    currency,
    position,
    vatRate,
  )

  return {serviceFee, ...(legalFee ? {legalFee} : {})}
}

export const calculateTmOwnershipTransferOrderPrice = (
  currency: Currency,
  trademarks: TmOwnershipTransferTrademarkDetails[],
  billedEntity: BillingEntityType,
  forceVatIncluded: boolean,
  includeAssignmentAgreementFee: boolean = false,
  billingEntityId: BillingEntityId = 'SK',
): TmOwnershipTransferOrderPrice => {
  const billingEntity = billingEntities[billingEntityId]

  const {vatIncluded, reverseVatCharge} = calculateVatStatus(
    billingEntity,
    billedEntity,
    forceVatIncluded,
  )
  const vatRate = forceVatIncluded || vatIncluded ? billingEntity.vatRate : 0

  const positionByJurisdiction: Partial<Record<Jurisdiction, number>> = {}

  const trademarkPrices = trademarks.map((trademark) => {
    const position = (positionByJurisdiction[trademark.jurisdiction] ?? 0) + 1
    positionByJurisdiction[trademark.jurisdiction] = position

    const {serviceFee, legalFee} = calculateTmOwnershipTransferTrademarkFees(
      trademark.jurisdiction,
      currency,
      position,
      vatRate,
    )

    return {
      jurisdiction: trademark.jurisdiction,
      trademarkNumber: trademark.registration_number,
      serviceFee,
      ...(legalFee ? {legalFee} : {}),
    }
  })

  // Billing view: collapse each jurisdiction into a single catalog-priced line.
  // Order of first appearance, so it lines up with the form summary.
  const jurisdictionOrder = [...new Set(trademarks.map((trademark) => trademark.jurisdiction))]

  const jurisdictionPrices = jurisdictionOrder.map((jurisdiction) => {
    const trademarkNumbers = trademarks
      .filter((trademark) => trademark.jurisdiction === jurisdiction)
      .map((trademark) => trademark.registration_number)

    const {serviceFee, legalFee} = calculateTmOwnershipTransferJurisdictionFees(
      jurisdiction,
      currency,
      trademarkNumbers.length,
      vatRate,
    )

    return {
      jurisdiction,
      numberOfTrademarks: trademarkNumbers.length,
      trademarkNumbers,
      serviceFee,
      ...(legalFee ? {legalFee} : {}),
    }
  })

  const assignmentAgreementFee = includeAssignmentAgreementFee
    ? calculateTmOwnershipTransferAssignmentAgreementFee(
        currency,
        billedEntity,
        forceVatIncluded,
        false,
        billingEntityId,
      )
    : undefined

  // Totalled from the billing lines (what is actually charged); the per-trademark
  // marginals sum to the same figure.
  const total = sumFees(
    jurisdictionPrices
      .flatMap((price) => [price.serviceFee, ...(price.legalFee ? [price.legalFee] : [])])
      .concat(assignmentAgreementFee ? [assignmentAgreementFee] : []),
  )

  return {
    trademarkPrices,
    jurisdictionPrices,
    ...(assignmentAgreementFee ? {assignmentAgreementFee} : {}),
    currency,
    vatIncluded,
    reverseVatCharge,
    vatRate,
    total,
  }
}

export const calculateTmOwnershipTransferAssignmentAgreementFee = (
  currency: Currency,
  billedEntity: BillingEntityType | undefined,
  forceVatIncluded: boolean,
  hasAssignmentAgreement: boolean,
  billingEntityId: BillingEntityId = 'SK',
): Fee => {
  const billingEntity = billingEntities[billingEntityId]
  const {vatIncluded} = calculateVatStatus(billingEntity, billedEntity, forceVatIncluded)
  const vatRate = forceVatIncluded || vatIncluded ? billingEntity.vatRate : 0

  // The enum values are the catalog product names, so the label (and product id)
  // come straight from the product.
  const serviceType = hasAssignmentAgreement
    ? AssignmentAgreementServiceType.review
    : AssignmentAgreementServiceType.drafting

  return buildProductFee(ASSIGNMENT_SERVICE_CATEGORY, serviceType, {currency, vatRate})
}

export const calculateTmOwnershipTransferJurisdictionsPrice = (
  jurisdictions: Jurisdiction[],
  currency: Currency,
  trademarksByJurisdiction: Partial<Record<Jurisdiction, number>>,
): TmOwnershipTransferJurisdictionsPrice => {
  const jurisdictionsPrice = jurisdictions.map((jurisdiction) => {
    const {serviceFee, legalFee, total} = calculateTmOwnershipTransferCountryPrice(
      jurisdiction,
      currency,
      trademarksByJurisdiction[jurisdiction] ?? 1,
    )

    return {jurisdiction, serviceFee, ...(legalFee ? {legalFee} : {}), total}
  })

  return {
    jurisdictions: jurisdictionsPrice,
    serviceFeeTotal: sumPrices(jurisdictionsPrice.map(({serviceFee}) => serviceFee.total)),
    legalFeeTotal: sumPrices(
      jurisdictionsPrice.flatMap(({legalFee}) => (legalFee ? [legalFee.total] : [])),
    ),
    total: sumPrices(jurisdictionsPrice.map(({total}) => total)),
  }
}
